import { PortableText, type PortableTextComponents, type PortableTextTypeComponentProps } from '@portabletext/react';
import type { PortableTextBlock, TypedObject } from '@portabletext/types';
import { linkMark } from '../marks';

export type TableCellValue = TypedObject & {
    value?: PortableTextBlock[];
};

export type TableRowValue = TypedObject & {
    cells?: TableCellValue[];
};

/** `table` block written by the Studio table plugin (@portabletext/plugin-table).
 *  `headerRows` counts the leading rows that render as <thead>. */
export type TableValue = TypedObject & {
    _type: 'table';
    headerRows?: number;
    rows?: TableRowValue[];
};

/** Cells hold the slim cell block from the Studio: paragraphs, lists and links —
 *  no block styles or class annotations, so the container's components would be
 *  dead weight here. */
const cellComponents: PortableTextComponents = {
    marks: {
        link: linkMark,
    },
};

function TableCell({ value }: { value?: PortableTextBlock[] }) {
    if (!value?.length) return null;
    return <PortableText value={value} components={cellComponents} />;
}

function TableCells({ row, as: Cell }: { row: TableRowValue; as: 'th' | 'td' }) {
    return (
        <>
            {(row.cells ?? []).map((cell, index) => (
                <Cell key={cell._key ?? index} scope={Cell === 'th' ? 'col' : undefined}>
                    <TableCell value={cell.value} />
                </Cell>
            ))}
        </>
    );
}

export function TableRenderer({ value }: PortableTextTypeComponentProps<TableValue>) {
    const rows = value?.rows ?? [];
    if (rows.length === 0) return null;

    const headerRows = Math.min(Math.max(Math.trunc(value?.headerRows ?? 0), 0), rows.length);
    const head = rows.slice(0, headerRows);
    const body = rows.slice(headerRows);

    // The wrapper scrolls so a wide table never widens the page; a scrollable
    // area needs to be keyboard reachable, hence tabIndex + a named region.
    return (
        <div className="prose-table" role="region" aria-label="Tabelle" tabIndex={0}>
            <table>
                {head.length > 0 && (
                    <thead>
                        {head.map((row, index) => (
                            <tr key={row._key ?? index}>
                                <TableCells row={row} as="th" />
                            </tr>
                        ))}
                    </thead>
                )}
                {body.length > 0 && (
                    <tbody>
                        {body.map((row, index) => (
                            <tr key={row._key ?? index}>
                                <TableCells row={row} as="td" />
                            </tr>
                        ))}
                    </tbody>
                )}
            </table>
        </div>
    );
}
